import { LvPageText, LvTextItem } from './lvPriceMatch';

/**
 * Textitems einer PDF-Seite zu sichtbaren Zeilen gruppieren.
 *
 * Warum eigenes Modul und nicht das vorhandene `clusterLines` aus
 * lvPriceMatch: dort geht es ums Stempeln von Betraegen, die Zeile ist nur
 * Anker. Hier ist die Zeile das ANZEIGEOBJEKT — der Nutzer klickt sie an. Sie
 * braucht deshalb eine stabile Kennung, ihre Ausdehnung und einen Text, der
 * genauso aussieht wie im PDF.
 *
 * Zwei Unterschiede zur Anker-Variante, beide gemessen an echten LV-PDFs:
 *  - Die Y-Toleranz ist relativ zur Schriftgroesse. Eine feste Toleranz von
 *    2,5 pt reisst eine 14-pt-Ueberschrift in zwei Zeilen und klebt zwei
 *    6-pt-Fusszeilen zusammen.
 *  - Textitems sind Fragmente, keine Woerter: Kerning und Ligaturen zerlegen
 *    "Baustelleneinrichtung" in mehrere Items. Stumpf mit Leerzeichen
 *    verbunden ergibt das "Baustellen ein richtung". Zusammengezogen wird
 *    deshalb anhand des tatsaechlichen Abstands.
 *
 * Rein und ohne pdfjs-Import, damit die Gruppierung ohne Worker und ohne DOM
 * pruefbar bleibt.
 */

export interface PdfTextLine {
    /** Stabile Kennung ueber Seiten hinweg: "<seite>:<zeile>", 0-basiert. */
    id: string;
    pageIndex: number;
    /** Baseline im PDF-User-Space (Ursprung unten links). */
    y: number;
    /** Linke und rechte Kante der Zeile. */
    x0: number;
    x1: number;
    /** Vorherrschende Schriftgroesse der Zeile in pt. */
    size: number;
    /** Zusammengezogener Text der Zeile. */
    text: string;
    items: LvTextItem[];
}

/** Fallback-Schriftgroesse, wenn das Dokument keine brauchbare liefert. */
const DEFAULT_SIZE = 9;
const MIN_SIZE = 4;
const MAX_SIZE = 28;

const plausibleSize = (n?: number): number | undefined =>
    n !== undefined && Number.isFinite(n) && n >= MIN_SIZE && n <= MAX_SIZE ? n : undefined;

/** Haeufigste plausible Schriftgroesse einer Itemgruppe. */
const dominantSize = (items: LvTextItem[]): number => {
    const counts = new Map<number, number>();
    for (const it of items) {
        const s = plausibleSize(it.fontSize);
        if (s === undefined) continue;
        const key = Math.round(s * 2) / 2;
        counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    if (!counts.size) return DEFAULT_SIZE;
    return [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0] - b[0])[0][0];
};

/**
 * Zieht die Fragmente einer Zeile zu lesbarem Text zusammen. Ein Leerzeichen
 * kommt nur dort, wo im PDF auch eine Luecke ist.
 */
export const mergeLineText = (items: LvTextItem[], size: number): string => {
    const gapThreshold = Math.max(0.8, size * 0.22);
    let out = '';
    let prevEnd: number | null = null;
    for (const it of items) {
        const s = it.str;
        if (!s) continue;
        if (prevEnd !== null) {
            const gap = it.x - prevEnd;
            const endsOpen = /\s$/.test(out);
            const startsSpace = /^\s/.test(s);
            if (gap > gapThreshold && !endsOpen && !startsSpace) out += ' ';
        }
        out += s;
        prevEnd = it.x + it.width;
    }
    return out.replace(/\s+/g, ' ').trim();
};

/**
 * Gruppiert die Items EINER Seite zu Zeilen, von oben nach unten.
 *
 * Die Toleranz richtet sich nach der Schriftgroesse des jeweils offenen
 * Zeilenanfangs — Hoch- und Tiefstellungen bleiben so bei ihrer Zeile,
 * getrennte Zeilen bleiben getrennt.
 */
export const groupPageLines = (items: LvTextItem[], pageIndex: number): PdfTextLine[] => {
    const usable = items.filter((it) => typeof it.str === 'string' && it.str.trim() !== '');
    if (!usable.length) return [];

    const sorted = [...usable].sort((a, b) => b.y - a.y || a.x - b.x);
    const groups: LvTextItem[][] = [];
    for (const it of sorted) {
        const last = groups[groups.length - 1];
        if (last) {
            const ref = plausibleSize(last[0].fontSize) ?? DEFAULT_SIZE;
            const tol = Math.max(1.5, ref * 0.45);
            if (Math.abs(last[0].y - it.y) <= tol) {
                last.push(it);
                continue;
            }
        }
        groups.push([it]);
    }

    return groups.map((group, i) => {
        const ordered = [...group].sort((a, b) => a.x - b.x);
        const size = dominantSize(ordered);
        return {
            id: `${pageIndex}:${i}`,
            pageIndex,
            // Baseline des ersten Items — die Zeile wird daran ausgerichtet.
            y: ordered[0].y,
            x0: Math.min(...ordered.map((t) => t.x)),
            x1: Math.max(...ordered.map((t) => t.x + t.width)),
            size,
            text: mergeLineText(ordered, size),
            items: ordered,
        };
    }).filter((l) => l.text !== '');
};

/** Alle Seiten in Dokumentreihenfolge. */
export const groupDocumentLines = (pages: LvPageText[]): PdfTextLine[][] =>
    pages.map((p, i) => groupPageLines(p.items, i));
